import { buttonBase, chipButtonBase } from "./dispatchConstants";
import { formatClock, formatQueueElapsed, prettyUnitStatus } from "./dispatchFormatters";
import {
  byPreferredUnitOrder,
  getUnitChipStyle,
  isAmbulance,
  isCourtesyDispatchEligible,
  isCourtesyRecommendedUnit,
} from "./dispatchUnitUtils";
import {
  getCallCardStyle,
  getCallHeaderStatus,
  getNextUnitActions,
  shouldShowDispatchControls,
} from "./dispatchCallUtils";
import { getSoftCardTheme } from "./useCardTheme";

function clean(value) {
  return String(value || "").trim();
}

function noteInputStyle() {
  return {
    width: "100%",
    fontSize: 9,
    padding: "4px 6px",
    borderRadius: 5,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#0f172a",
    boxSizing: "border-box",
    minHeight: 22,
  };
}

function isAtHQ(call) {
  return (
    call.patientAtHQ === true ||
    call.status === "arrivedHQ" ||
    !!call.hq?.bedNumber ||
    !!call.hq?.firstName
  );
}

function getThemeKey(call) {
  const courtesyFlow = call.callType === "courtesyTransport" || !!call.hq?.courtesyRequested;
  const queued = call.status === "queued";
  const atHQ = isAtHQ(call);
  const ambulanceRequested = !!call.hospitalTransport?.requested;
  const unitStatuses = Object.values(call.unitStatuses || {});

  const transportingToHQ =
    !atHQ && (call.status === "transporting" || unitStatuses.includes("TO_HQ"));

  const activeEmergency =
    call.callType === "emergency" &&
    !atHQ &&
    !transportingToHQ &&
    ["dispatched", "onScene"].includes(call.status);

  if (call.hq?.courtesyQueued === true) return "queuedCourtesy";
  if (courtesyFlow && queued) return "queuedCourtesy";
  if (ambulanceRequested) return "awaitingAmbulance";
  if (courtesyFlow) return "awaitingCourtesy";
  if (atHQ) return "atHQ";
  if (transportingToHQ) return "transportingToHQ";
  if (activeEmergency) return "activeEmergency";
  return "default";
}

function isHQAmbulanceAwaitingUnit(call) {
  return (
    !!call?.hospitalTransport?.requested &&
    !call?.hospitalTransport?.ambulanceUnitId &&
    !call?.ambulanceUnitId
  );
}

function isHQCourtesyAwaitingUnit(call) {
  return (
    call?.callType === "courtesyTransport" &&
    (!Array.isArray(call?.assignedUnitIds) ||
      call.assignedUnitIds.length === 0)
  );
}

function getQueueDisplayStatus(call, rawHeaderStatus) {
if (call.hq?.courtesyQueued === true) {
  return "Queued";
}

  if (isHQAmbulanceAwaitingUnit(call)) {
    return call?.hospitalTransport?.acknowledged === false
      ? "Request Received"
      : "Awaiting Available Unit";
  }

  if (isHQCourtesyAwaitingUnit(call)) {
    return call?.hq?.courtesyAcknowledged === false
      ? "Request Received"
      : "Awaiting Available Unit";
  }

  if (call.status === "queued") return "Queued";
  return rawHeaderStatus;
}

function getQueueTimestamp(call) {
  return (
    call?.hospitalTransport?.requestedAt ||
    call?.hq?.courtesyRequestedAt ||
    call?.queuedAt ||
    call?.createdAt
  );
}

export function DispatchCallCard({
  call,
  units,
  availableUnits,
  nowMs,
  openUnitMenuKey,
  setOpenUnitMenuKey,
  openAddUnitCallId,
  setOpenAddUnitCallId,
  onLoadQueuedCallIntoForm,
  onResolveInField,
  onCancelCall,
  onAcknowledgeAmbulance,
  onAcknowledgeCourtesy,
  onQueueCourtesy,
  onUpdateUnitOnCall,
  onAddUnitToCall,
}) {
  const queued =
    call.status === "queued" ||
    isHQAmbulanceAwaitingUnit(call) ||
    isHQCourtesyAwaitingUnit(call);

  const atHQ = isAtHQ(call);
  const baseCardStyle = getCallCardStyle(call, nowMs);
  const rawHeaderStatus = getCallHeaderStatus(call);
  const showDispatchButtons = shouldShowDispatchControls(call);

 const ambulanceOnlyFlow =
  !!call.hospitalTransport?.requested;

  const courtesyFlow = call.callType === "courtesyTransport" || !!call.hq?.courtesyRequested;
  const theme = getSoftCardTheme(getThemeKey(call));

  const cardStyle = {
    ...baseCardStyle,
    ...theme,
  };

  const callUnits = (call.assignedUnitIds || [])
    .map((id) => units.find((u) => u.id === id))
    .filter(Boolean)
    .sort(byPreferredUnitOrder);

  const addableUnits = availableUnits
    .filter((u) => {
      if ((call.assignedUnitIds || []).includes(u.id)) return false;
      if (ambulanceOnlyFlow) return isAmbulance(u);
      if (courtesyFlow) return isCourtesyDispatchEligible(u);
      return true;
    })
    .sort(byPreferredUnitOrder);

    const recommendedCourtesyUnit =
  courtesyFlow ? addableUnits.find((u) => isCourtesyRecommendedUnit(u)) : null;

const hqCourtesyAwaitingUnit =
  call?.callType === "courtesyTransport" && !callUnits.length;
  const hqAmbulanceAwaitingUnit =
  !!call?.hospitalTransport?.requested &&
  !call?.hospitalTransport?.ambulanceUnitId &&
  !call?.ambulanceUnitId &&
  !callUnits.length;
  const pickup = call.hq?.courtesyPickupLocation || call.pickupLocation || "—";
  const dropoff =
    call.hq?.courtesyDestination ||
    call.dropoffLocation ||
    call.hospitalTransport?.destinationHospital ||
    "—";

  const passengers =
    Number(call.hq?.courtesyPassengerCount || call.passengerCount || 0) > 0
      ? Number(call.hq?.courtesyPassengerCount || call.passengerCount || 0)
      : null;

  const patientName = clean(call.hq?.firstName);
  const bedNumber = clean(call.hq?.bedNumber);
  const chiefComplaint = clean(call.hq?.chiefComplaint);
  const hasPatientHeaderInfo = !!(patientName || bedNumber || chiefComplaint);

  const title = hasPatientHeaderInfo
    ? [patientName || "Unnamed Patient", bedNumber ? `Bed ${bedNumber}` : ""]
        .filter(Boolean)
        .join(" • ")
    : courtesyFlow
      ? "Courtesy Transport"
      : `${call.location || "—"} • ${call.nature || "—"}`;

  const displayStatus = queued ? getQueueDisplayStatus(call, rawHeaderStatus) : rawHeaderStatus;
  const queueTs = getQueueTimestamp(call);

  const secondaryLine = queued
    ? `${displayStatus} • ${formatClock(queueTs)}${
        queueTs ? ` • ${formatQueueElapsed(queueTs, nowMs)}` : ""
      }`
    : atHQ
      ? [
          chiefComplaint || "",
          `${rawHeaderStatus} • ${formatClock(call.arrivedDestinationAt || call.createdAt)}`,
        ]
          .filter(Boolean)
          .join(" • ")
      : `${rawHeaderStatus} • ${formatClock(call.createdAt)}`;

  return (
    <div
      style={{
        border: `2px solid ${cardStyle.borderColor}`,
        background: cardStyle.bgColor,
        borderRadius: 10,
        padding: 5,
        boxShadow: "0 2px 8px rgba(15, 23, 42, 0.08)",
        minWidth: 0,
        display: "grid",
        gap: 4,
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: 5,
          alignItems: "start",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontWeight: 900,
              fontSize: 10,
              color: cardStyle.titleColor,
              lineHeight: 1.05,
              minWidth: 0,
              wordBreak: "break-word",
            }}
          >
            {title}
          </div>

          <div
            style={{
              fontSize: 10,
              color: cardStyle.statusColor,
              fontWeight: 900,
              marginTop: 2,
              lineHeight: 1.08,
              wordBreak: "break-word",
            }}
          >
            {secondaryLine}
          </div>
        </div>

        {showDispatchButtons && (
          <div
            style={{
              display: "flex",
              gap: 4,
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            {call.status === "queued" && (
              <button
                type="button"
                onClick={() => onLoadQueuedCallIntoForm(call)}
                style={compactButtonStyle}
              >
                Dispatch
              </button>
            )}

            {!queued && call.callType === "emergency" && call.status !== "arrivedHQ" && (
              <button
                type="button"
                onClick={() => onResolveInField(call)}
                style={compactButtonStyle}
              >
                Resolve
              </button>
            )}

            {call.status !== "arrivedHQ" && (
              <button
                type="button"
                onClick={() => onCancelCall(call)}
                style={{
                  ...compactButtonStyle,
                  background: "#fee2e2",
                  borderColor: "#fca5a5",
                  color: "#b91c1c",
                }}
              >
                Cancel
              </button>
            )}
          </div>
        )}
      </div>

      {(bedNumber || chiefComplaint) && (
        <div
          style={{
            padding: "5px 7px",
            borderRadius: 7,
            background: "#ffffff",
            border: `1px solid ${cardStyle.dividerColor}`,
            display: "grid",
            gap: 1,
          }}
        >
          {!!bedNumber && <div style={smallInfoStyle}>Bed: {bedNumber}</div>}
          {!!chiefComplaint && <div style={smallInfoStyle}>Complaint: {chiefComplaint}</div>}
        </div>
      )}

      {courtesyFlow && (
        <div
          style={{
            padding: "4px 6px",
            borderRadius: 6,
            background: "#ffffff",
            border: `1px solid ${cardStyle.dividerColor}`,
            display: "grid",
            gap: 3,
          }}
        >
          <div style={{ fontSize: 9, fontWeight: 700, color: "#334155", lineHeight: 1.15 }}>
            PU: {pickup} • DO: {dropoff}
            {passengers ? ` • ${passengers} pax` : ""}
          </div>

          <input
            value={call.notes || ""}
            onChange={(e) => onUpdateUnitOnCall(call, "__editNotes", e.target.value)}
            placeholder="Crew notes..."
            style={{
              fontSize: 9,
              padding: "2px 6px",
              borderRadius: 4,
              border: "1px solid #cbd5e1",
              height: 22,
            }}
          />
        </div>
      )}

      {!courtesyFlow && !atHQ && (
        <div
          style={{
            padding: "4px 6px",
            borderRadius: 6,
            background: "#ffffff",
            border: `1px solid ${cardStyle.dividerColor}`,
            display: "grid",
            gap: 2,
          }}
        >
          <div style={smallInfoStyle}>Notes:</div>
          <input
            value={call.notes || ""}
            onChange={(e) => onUpdateUnitOnCall(call, "__editNotes", e.target.value)}
            placeholder="Add notes for crew..."
            style={noteInputStyle()}
          />
        </div>
      )}

      {!courtesyFlow && !!call.hospitalTransport?.requested && (
        <div style={smallInfoStyle}>
          Destination: {call.hospitalTransport?.destinationHospital || "—"}
        </div>
      )}

      {!queued &&
        call.hospitalTransport?.requested &&
        call.hospitalTransport?.acknowledged === false && (
          <div style={actionRowStyle(cardStyle.dividerColor)}>
            <div style={{ fontSize: 10, fontWeight: 800, color: "#b91c1c", lineHeight: 1.05 }}>
              Ambulance requested
            </div>
            <button
              type="button"
              onClick={() => onAcknowledgeAmbulance(call)}
              style={{
                ...compactButtonStyle,
                background: "#fee2e2",
                borderColor: "#fca5a5",
                color: "#b91c1c",
              }}
            >
              {availableUnits.some((u) => isAmbulance(u)) ? "Ack + Add" : "Ack"}
            </button>
          </div>
        )}

      {!queued && call.hq?.courtesyRequested && call.hq?.courtesyAcknowledged === false && (
        <div style={actionRowStyle(cardStyle.dividerColor)}>
          <div style={{ fontSize: 10, fontWeight: 800, color: "#a16207", lineHeight: 1.05 }}>
            Courtesy requested
          </div>
          <button
            type="button"
            onClick={() => onAcknowledgeCourtesy(call)}
            style={{
              ...compactButtonStyle,
              background: "#fef3c7",
              borderColor: "#fde68a",
              color: "#92400e",
            }}
          >
            Ack
          </button>
        </div>
      )}

      {!!callUnits.length && (
        <div style={actionRowStyle(cardStyle.dividerColor)}>
          <div style={sectionLabelStyle}>Units</div>

          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {callUnits.map((unit) => {
              const currentStatus = call.unitStatuses?.[unit.id] || unit.status || "—";
              const chipStyle = getUnitChipStyle(currentStatus);
              const menuKey = `${call.id}:${unit.id}`;
              const open = openUnitMenuKey === menuKey;
             const nextActions = getNextUnitActions(unit, call, currentStatus);

if (unit.name === "CART 5" || unit.name === "Cart 5") {
  console.log("CART 5 DEBUG", {
    unit,
    callId: call.id,
    callType: call.callType,
    currentStatus,
    nextActions,
    unitStatusFromCall: call.unitStatuses?.[unit.id],
    unitStatusFromUnit: unit.status,
  });
}

              return (
                <div key={unit.id} style={{ position: "relative" }}>
                  <button
                    type="button"
                    onClick={() => setOpenUnitMenuKey(open ? "" : menuKey)}
                    style={{
                      ...chipButtonBase,
                      background: open ? "#1d4ed8" : chipStyle.background,
                      borderColor: open ? "#1d4ed8" : chipStyle.borderColor,
                      color: open ? "#ffffff" : chipStyle.color,
                      boxShadow: open ? "0 0 0 2px rgba(29, 78, 216, 0.4)" : "none",
                      padding: "2px 6px",
                      fontSize: 9,
                      fontWeight: 800,
                      minHeight: 20,
                      lineHeight: 1.05,
                    }}
                  >
                    {unit.name} • {
                    (() => {
                      if (courtesyFlow) {
                        switch (currentStatus) {
                          case "DISP":
                            return "Responding";
                          case "ON_SCENE":
                            return "On-Scene";
                          case "TO_HQ":
                          case "TRANSPORTING":
                            return "Transporting";
                          case "ARRIVED":
                          case "ARRIVED_HQ":
                            return "Arrived";
                          default:
                            return prettyUnitStatus(currentStatus);
                        }
                      }

                      // Emergency calls (leave mostly as-is but clean)
                      switch (currentStatus) {
                        case "DISP":
                          return "Responding";
                        case "ON_SCENE":
                          return "On-Scene";
                        case "TO_HQ":
                          return "To HQ";
                        case "TRANSPORTING":
                          return "Transporting";
                        case "ARRIVED":
                          return "Arrived";
                        case "ARRIVED_HQ":
                          return "Arrived HQ";
                        default:
                          return prettyUnitStatus(currentStatus);
                      }
                    })()
                  }
                  </button>

                 {open && !!nextActions.length && (
                  <div style={menuStyle}>
                    <div style={{
                      fontSize: 10,
                      fontWeight: 800,
                      color: "#334155",
                      width: "100%",
                      marginBottom: 4
                    }}>
                      {unit.name} Status
                    </div>
                      {nextActions.map((status) => (
                        <button
                          key={status}
                          type="button"
                          onClick={() => {
                            setOpenUnitMenuKey("");
                            onUpdateUnitOnCall(call, unit.id, status);
                          }}
                          style={compactButtonStyle}
                        >
                         {status === "ON_SCENE"
                    ? "On Scene"
                    : status === "TRANSPORTING" || status === "TO_HQ"
                      ? ambulanceOnlyFlow
                        ? "Transporting to Hospital"
                        : courtesyFlow
                          ? "Transporting"
                          : "Transporting"
                      : status === "ARRIVED" || status === "ARRIVED_HQ"
                        ? ambulanceOnlyFlow
                          ? "PT Transported to Hosp"
                          : courtesyFlow
                            ? "Arrived"
                            : prettyUnitStatus(status)
                        : status === "AVAIL"
                          ? "Avail"
                          : status === "CLEAR"
                          ? "Clear"
                          : prettyUnitStatus(status)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {!!addableUnits.length && (
              <div style={{ position: "relative" }}>
                <button
                  type="button"
                  onClick={() => setOpenAddUnitCallId(openAddUnitCallId === call.id ? "" : call.id)}
                  style={{
                    ...chipButtonBase,
                    background: cardStyle.chipAddBg,
                    borderColor: cardStyle.chipAddBorder,
                    color: cardStyle.chipAddText,
                    padding: "2px 6px",
                    fontSize: 9,
                    fontWeight: 800,
                    minHeight: 20,
                    lineHeight: 1.05,
                  }}
                >
                  + Add Unit
                </button>

                {openAddUnitCallId === call.id && (
                  <div style={menuStyle}>
                    {addableUnits.map((unit) => (
                      <button
                        key={unit.id}
                        type="button"
                        onClick={() => {
                          setOpenAddUnitCallId("");
                          onAddUnitToCall(call, unit.id);
                        }}
                        style={compactButtonStyle}
                      >
                        {unit.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

     {!callUnits.length && !!addableUnits.length && (
  <div style={actionRowStyle(cardStyle.dividerColor)}>
    <div style={sectionLabelStyle}>
  {hqAmbulanceAwaitingUnit
    ? "Ambulance Requested"
    : hqCourtesyAwaitingUnit
      ? "Courtesy Transport Requested"
      : "No units assigned"}
</div>

{hqAmbulanceAwaitingUnit && (
  <>
    <button
      type="button"
      onClick={() =>
        setOpenAddUnitCallId(openAddUnitCallId === call.id ? "" : call.id)
      }
      style={{
        ...compactButtonStyle,
        background: "#fee2e2",
        borderColor: "#fca5a5",
        color: "#b91c1c",
      }}
    >
      Assign Ambulance
    </button>
  </>
)}

    {hqCourtesyAwaitingUnit && (
      <>
        <button
          type="button"
          onClick={() => {
            if (recommendedCourtesyUnit) {
              onAddUnitToCall(call, recommendedCourtesyUnit.id);
            } else {
              alert("Transport is not available. Choose Assign Other Unit or Queue.");
            }
          }}
          style={{
            ...compactButtonStyle,
            background: "#dcfce7",
            borderColor: "#86efac",
            color: "#166534",
          }}
        >
          Dispatch Transport
        </button>

        <button
          type="button"
          onClick={() =>
            setOpenAddUnitCallId(openAddUnitCallId === call.id ? "" : call.id)
          }
          style={{
            ...compactButtonStyle,
            background: "#eff6ff",
            borderColor: "#bfdbfe",
            color: "#1d4ed8",
          }}
        >
          Assign Other Unit
        </button>

        <button
          type="button"
          onClick={() => onQueueCourtesy(call)}
          style={{
            ...compactButtonStyle,
            background: "#f8fafc",
            borderColor: "#cbd5e1",
            color: "#334155",
          }}
        >
          Queue
        </button>
      </>
    )}

{openAddUnitCallId === call.id && (
  <div
    onClick={() => setOpenAddUnitCallId("")}
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 99999,
      background: "rgba(15, 23, 42, 0.55)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 16,
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        width: "min(520px, 95vw)",
        maxHeight: "80vh",
        overflowY: "auto",
        background: "#ffffff",
        border: "1px solid #cbd5e1",
        borderRadius: 12,
        padding: 12,
        boxShadow: "0 20px 50px rgba(15, 23, 42, 0.35)",
        display: "grid",
        gap: 8,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
          borderBottom: "1px solid #e2e8f0",
          paddingBottom: 8,
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 900, color: "#0f172a" }}>
          Select Unit
        </div>

        <button
          type="button"
          onClick={() => setOpenAddUnitCallId("")}
          style={{
            ...compactButtonStyle,
            background: "#f8fafc",
            borderColor: "#cbd5e1",
          }}
        >
          Close
        </button>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: 8,
        }}
      >
        {addableUnits.map((unit) => (
          <button
            key={unit.id}
            type="button"
            onClick={() => {
              setOpenAddUnitCallId("");
              onAddUnitToCall(call, unit.id);
            }}
            style={{
              ...compactButtonStyle,
              minHeight: 42,
              fontSize: 12,
              justifyContent: "center",
              background: "#eff6ff",
              borderColor: "#bfdbfe",
              color: "#1d4ed8",
            }}
          >
            {unit.name}
          </button>
        ))}
      </div>
    </div>
  </div>
)}
  
  </div>
)}
    </div>
  );
}

const compactButtonStyle = {
  ...buttonBase,
  padding: "3px 7px",
  fontSize: 9,
  fontWeight: 800,
  minHeight: 22,
  lineHeight: 1.05,
  background: "#ffffff",
  borderColor: "#cbd5e1",
  color: "#0f172a",
};

const actionRowStyle = (dividerColor = "#e2e8f0") => ({
  paddingTop: 4,
  borderTop: `1px solid ${dividerColor}`,
  display: "flex",
  gap: 4,
  flexWrap: "wrap",
  alignItems: "center",
});

const sectionLabelStyle = {
  fontSize: 9,
  fontWeight: 800,
  color: "#475569",
  lineHeight: 1.05,
};

const smallInfoStyle = {
  fontSize: 9,
  color: "#334155",
  lineHeight: 1.1,
  fontWeight: 700,
};

const menuStyle = {
  position: "absolute",
  bottom: "calc(100% + 4px)",
  top: "auto",
  left: 0,
  zIndex: 9999,
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: 4,
  background: "#ffffff",
  border: "1px solid #cbd5e1",
  borderRadius: 7,
  padding: 6,
  boxShadow: "0 8px 20px rgba(15, 23, 42, 0.14)",
  minWidth: 150,
  maxHeight: 220,
  overflowY: "auto",
};